import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, BlockStack, Collapsible, Text } from '@shopify/polaris';
import { Card } from '@/components/card/card';
import { ChevronDown, Frown, Smile, ThumbsUp } from 'lucide-react';
import { submitHelpFeedback } from '@/api/help-api';

// Why thresholds: avoids showing TOC when it's noise (< 4 items)
// and avoids above-the-fold bloat when there are many headings (6+)
const MIN_TOC_ITEMS = 4;
const COLLAPSE_THRESHOLD = 6;
const PREVIEW_COUNT = 3;

/**
 * Help Article Component
 *
 * Renders article with TOC scroll spy, emoji feedback, and related articles.
 *
 * Why .hc-* classes instead of CSS modules: single source of truth in
 * resources/css/help-center.scss shared with Blade SSR.
 *
 * Location: resources/react/src/modules/help/components/help-article.jsx
 *
 * @component
 *
 * @param {Object} props
 * @param {Object} props.article - Article data from API
 * @param {string} props.article.title - Article title
 * @param {string} props.article.description - Article description
 * @param {string} props.article.html - Article HTML content
 * @param {Array} props.article.toc - Table of contents
 * @param {string} props.article.updated_at - Last update date
 * @param {string} props.article.category - Article category
 * @param {string} props.article.slug - Article slug
 */
export const HelpArticle = ({ article }) => {
  const navigate = useNavigate();
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  // Why separate feedbackValue: tracks helpful/not-helpful before optional reason step
  const [feedbackValue, setFeedbackValue] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [showReasonField, setShowReasonField] = useState(false);
  const [activeTocItem, setActiveTocItem] = useState(null);
  const [tocExpanded, setTocExpanded] = useState(false);
  const contentRef = useRef(null);

  // Scroll spy for TOC
  useEffect(() => {
    if (!article.toc || article.toc.length === 0) return;

    const handleScroll = () => {
      const headings = article.toc.map((item) =>
        document.getElementById(item.slug)
      );

      const currentHeading = headings.find((heading) => {
        if (!heading) return false;
        const rect = heading.getBoundingClientRect();
        return rect.top >= 0 && rect.top <= 150;
      });

      if (currentHeading) {
        setActiveTocItem(currentHeading.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article.toc]);

  // Why useEffect for copy buttons: injects "Copy" button into rendered <pre> elements
  // after article HTML is mounted to the DOM via dangerouslySetInnerHTML
  useEffect(() => {
    if (!contentRef.current) return;

    const pres = contentRef.current.querySelectorAll('pre');
    pres.forEach((pre) => {
      // Why skip if already wrapped: prevents duplicate buttons on re-render
      if (pre.parentNode.classList.contains('hc-code-block')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'hc-code-block';
      pre.parentNode.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);

      const btn = document.createElement('button');
      btn.className = 'hc-code-copy';
      btn.textContent = 'Copy';
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Copy code to clipboard');
      wrapper.appendChild(btn);

      btn.addEventListener('click', () => {
        const code = pre.querySelector('code');
        const text = code ? code.textContent : pre.textContent;
        // Why catch: Clipboard API can fail on HTTP, in iframes, or when permission denied
        navigator.clipboard
          .writeText(text)
          .then(() => {
            btn.textContent = 'Copied!';
            btn.classList.add('hc-code-copy--copied');
            setTimeout(() => {
              btn.textContent = 'Copy';
              btn.classList.remove('hc-code-copy--copied');
            }, 2000);
          })
          .catch(() => {
            btn.textContent = 'Failed';
            setTimeout(() => {
              btn.textContent = 'Copy';
            }, 2000);
          });
      });
    });
  }, [article.html]);

  // Why useEffect for lightbox: injects click-to-zoom behavior on all <img> elements
  // inside article HTML. Uses event delegation (single listener on container)
  // for consistency with the Blade SSR version and simpler cleanup.
  useEffect(() => {
    if (!contentRef.current) return;

    const container = contentRef.current;

    const handleClick = (e) => {
      const img = e.target.closest('img');
      if (!img) return;

      // Why preventDefault: if image is wrapped in <a>, prevents navigation
      e.preventDefault();

      // Why guard: prevents stacking multiple lightboxes on rapid clicks
      if (document.querySelector('.hc-lightbox')) return;

      // Why store trigger: restores focus to the clicked image on close (WCAG 2.4.3)
      const triggerElement = img;

      const overlay = document.createElement('div');
      overlay.className = 'hc-lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', img.alt || 'Enlarged image');

      const lbImg = document.createElement('img');
      lbImg.className = 'hc-lightbox__img';
      lbImg.src = img.src;
      lbImg.alt = img.alt || '';

      const closeBtn = document.createElement('button');
      closeBtn.className = 'hc-lightbox__close';
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Close image');
      closeBtn.innerHTML = '&#10005;';

      const close = () => {
        overlay.remove();
        // Why restore overflow: re-enables page scroll after lightbox closes
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKeyDown);
        // Why restore focus: returns keyboard focus to the image that opened the lightbox
        triggerElement.focus();
      };

      // Why keydown on document: Escape closes lightbox regardless of focus position,
      // Tab is trapped within the lightbox (only the close button is focusable)
      const onKeyDown = (ev) => {
        if (ev.key === 'Escape') close();
        // Why trap Tab: prevents keyboard focus from leaving the dialog overlay
        if (ev.key === 'Tab') {
          ev.preventDefault();
          closeBtn.focus();
        }
      };

      overlay.addEventListener('click', (ev) => {
        // Why target check: clicking the image itself should not close the lightbox
        if (ev.target === overlay) close();
      });
      closeBtn.addEventListener('click', close);
      document.addEventListener('keydown', onKeyDown);

      // Why overflow hidden: locks page scroll while lightbox is open (especially mobile)
      document.body.style.overflow = 'hidden';

      overlay.appendChild(lbImg);
      overlay.appendChild(closeBtn);
      document.body.appendChild(overlay);

      // Why focus closeBtn: moves focus into the dialog for keyboard accessibility
      closeBtn.focus();
    };

    container.addEventListener('click', handleClick);

    // Why cleanup: removes listener when article content changes or component unmounts
    return () => {
      container.removeEventListener('click', handleClick);
      // Why remove stale overlay + restore scroll: if component unmounts while lightbox is open
      const staleOverlay = document.querySelector('.hc-lightbox');
      if (staleOverlay) {
        staleOverlay.remove();
        document.body.style.overflow = '';
      }
    };
  }, [article.html]);

  // Why deferred submission: stores vote locally first, then sends a single API call
  // when user submits reason or skips — avoids duplicate EventLogger entries
  const handleFeedback = (helpful) => {
    setFeedbackValue(helpful);
    setShowReasonField(true);
  };

  // Why single API call: sends both vote and optional reason together to prevent
  // double EventLogger entries (review issue #3)
  const handleReasonSubmit = async () => {
    setSubmittingFeedback(true);
    try {
      await submitHelpFeedback(
        article.category,
        article.slug,
        feedbackValue,
        feedbackReason.trim() || undefined
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setSubmittingFeedback(false);
      setFeedbackSubmitted(true);
    }
  };

  const scrollToHeading = (slug) => {
    const element = document.getElementById(slug);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Mock related articles (replace with API call later)
  const relatedArticles = [
    {
      title: 'Managing Subscriptions',
      category: 'subscriptions',
      slug: 'managing-subscriptions',
      description: 'View, filter, and manage customer subscriptions',
    },
    {
      title: 'Widget Installation',
      category: 'getting-started',
      slug: 'widget-installation',
      description: 'Install the Notify Me widget on your store',
    },
  ];

  return (
    <BlockStack gap="400">
      {/* Article Header */}
      <Card>
        <div className="hc-article__header">
          <h1 className="hc-article__title">{article.title}</h1>

          {article.description && (
            <p className="hc-article__description">{article.description}</p>
          )}

          {article.updated_at && (
            <div className="hc-article__meta">
              <Text variant="bodySm" tone="subdued">
                Last updated:{' '}
                {new Date(article.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                {/* Why estimated_time in meta: sets reader expectations per UX manifest */}
                {article.estimated_time && (
                  <>
                    <span className="hc-article__meta-separator">&middot;</span>
                    <span>{article.estimated_time}</span>
                  </>
                )}
              </Text>
            </div>
          )}
        </div>
      </Card>

      {/* Table of Contents — lightweight utility, not a content card */}
      {/* Why MIN_TOC_ITEMS threshold: articles with < 4 headings are short enough to scan without TOC */}
      {article.toc &&
        article.toc.length >= MIN_TOC_ITEMS &&
        (() => {
          const needsCollapse = article.toc.length >= COLLAPSE_THRESHOLD;
          const previewItems = needsCollapse
            ? article.toc.slice(0, PREVIEW_COUNT)
            : article.toc;
          const hiddenItems = needsCollapse
            ? article.toc.slice(PREVIEW_COUNT)
            : [];

          return (
            <div className="hc-toc">
              <div className="hc-toc__title">Contents</div>
              <nav className="hc-toc__list" aria-label="Table of contents">
                {previewItems.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    className={`hc-toc__link hc-toc__link--level-${item.level} ${
                      activeTocItem === item.slug ? 'hc-toc__link--active' : ''
                    }`}
                    onClick={() => scrollToHeading(item.slug)}
                  >
                    {item.text}
                  </button>
                ))}

                {/* Why Collapsible: Polaris built-in animation (200ms), already used in billing components */}
                {needsCollapse && (
                  <>
                    <Collapsible
                      id="toc-collapsible"
                      open={tocExpanded}
                      transition={{ duration: '200ms' }}
                    >
                      {hiddenItems.map((item) => (
                        <button
                          key={item.slug}
                          type="button"
                          className={`hc-toc__link hc-toc__link--level-${item.level} ${
                            activeTocItem === item.slug
                              ? 'hc-toc__link--active'
                              : ''
                          }`}
                          onClick={() => scrollToHeading(item.slug)}
                        >
                          {item.text}
                        </button>
                      ))}
                    </Collapsible>

                    <button
                      type="button"
                      className="hc-toc__toggle"
                      onClick={() => setTocExpanded((prev) => !prev)}
                      aria-expanded={tocExpanded}
                      aria-controls="toc-collapsible"
                    >
                      <span>
                        {tocExpanded
                          ? 'Show less'
                          : `Show all sections (${article.toc.length})`}
                      </span>
                      <span
                        className={`hc-toc__toggle-icon ${
                          tocExpanded ? 'hc-toc__toggle-icon--expanded' : ''
                        }`}
                      >
                        <ChevronDown size={14} />
                      </span>
                    </button>
                  </>
                )}
              </nav>
            </div>
          );
        })()}

      {/* Article Content */}
      <Card>
        <div
          ref={contentRef}
          className="hc-content"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: article.html }}
        />
      </Card>

      {/* Feedback Section with Emoji Reactions + Optional Reason */}
      <Card>
        {feedbackSubmitted ? (
          <Banner tone="success">
            <div className="hc-feedback__success">
              <ThumbsUp size={20} strokeWidth={2} />
              <span>Thank you for your feedback!</span>
            </div>
          </Banner>
        ) : showReasonField ? (
          /* Why reason field after vote: collects actionable context without blocking initial signal */
          <div className="hc-feedback">
            <div className="hc-feedback__success" style={{ marginBottom: 12 }}>
              <ThumbsUp size={20} strokeWidth={2} />
              <span>Thanks for the feedback!</span>
            </div>
            <div className="hc-feedback__reason">
              <label
                className="hc-feedback__reason-label"
                htmlFor="feedback-reason"
              >
                Want to tell us more? (optional)
              </label>
              <textarea
                id="feedback-reason"
                className="hc-feedback__reason-textarea"
                placeholder="What could we improve?"
                value={feedbackReason}
                onChange={(e) => setFeedbackReason(e.target.value)}
                maxLength={500}
              />
              <button
                type="button"
                className="hc-feedback__reason-submit"
                onClick={handleReasonSubmit}
                disabled={submittingFeedback}
              >
                {feedbackReason.trim() ? 'Submit' : 'Skip'}
              </button>
            </div>
          </div>
        ) : (
          <div className="hc-feedback">
            <Text variant="headingSm" as="h3">
              Was this article helpful?
            </Text>
            <div className="hc-feedback__buttons">
              <button
                type="button"
                className="hc-feedback__button"
                onClick={() => handleFeedback(true)}
                disabled={submittingFeedback}
              >
                <Smile size={24} strokeWidth={2} />
                <span>Yes, it helped!</span>
              </button>
              <button
                type="button"
                className="hc-feedback__button"
                onClick={() => handleFeedback(false)}
                disabled={submittingFeedback}
              >
                <Frown size={24} strokeWidth={2} />
                <span>Not really</span>
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="hc-related">
          <Text variant="headingMd" as="h2">
            Related Articles
          </Text>
          <div className="hc-related__grid">
            {relatedArticles.map((item) => (
              <Card key={`${item.category}-${item.slug}`}>
                <button
                  type="button"
                  className="hc-related__card"
                  onClick={() =>
                    navigate(`/help/${item.category}/${item.slug}`)
                  }
                >
                  <h4 className="hc-related__title">{item.title}</h4>
                  <p className="hc-related__description">{item.description}</p>
                  <span className="hc-related__arrow">→</span>
                </button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </BlockStack>
  );
};
